/**
 * Portal money-question pages — deterministic, dead-simple answers driven
 * by the client's Xero/Prosaic snapshot. No AI, just clear maths so the
 * owner can point at a number and act.
 *
 * Four routes exported:
 *   PortalPaySelf      → /portal/pay-self
 *   PortalHire         → /portal/hire
 *   PortalPriceChange  → /portal/price-change
 *   PortalGrowthPlan   → /portal/growth-plan
 *
 * Every page shares the same shape: header → one input → live answer card
 * → transparent maths → "get your accountant to double-check" nudge. The
 * snapshot is what powers the sensible defaults.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Wallet, UserPlus, TrendingUp, Rocket, Loader2, AlertTriangle } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 });
const PCT = new Intl.NumberFormat("en-NZ", { style: "percent", maximumFractionDigits: 1 });

function useSnapshot() {
    const { activeClientId, combinedView } = useAuth();
    const [snap, setSnap] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let alive = true;
        setLoading(true);
        // Combined view fans out across all entities; single view scopes to
        // the active client. Both endpoints now return a `money_basics` block
        // with monthly_revenue / monthly_expenses / annual_revenue.
        const req = combinedView
            ? api.get("/portal/snapshot/combined")
            : api.get("/portal/snapshot", { params: activeClientId ? { client_id: activeClientId } : {} });
        req
            .then(({ data }) => { if (alive) { setSnap(data); setLoading(false); } })
            .catch(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [activeClientId, combinedView]);
    return { snap, loading };
}

function readBasics(snap) {
    const b = snap?.money_basics || {};
    return {
        monthly_revenue: Number(b.monthly_revenue || 0),
        monthly_expenses: Number(b.monthly_expenses || 0),
        annual_revenue: Number(b.annual_revenue || (Number(b.monthly_revenue || 0) * 12)),
    };
}

function PageShell({ title, blurb, icon: Icon, gradient, testId, children }) {
    return (
        <div className="space-y-5 pb-10" data-testid={testId}>
            <Link to="/portal" className="inline-flex items-center gap-1.5 text-xs text-[#5E636E] hover:text-[#0F1115] transition-colors">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to home
            </Link>
            <div className={`rounded-2xl bg-gradient-to-br ${gradient} text-white p-5 sm:p-6`}>
                <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-medium">
                    <Icon className="h-3.5 w-3.5" /> Uses your data
                </div>
                <h1 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-tight">{title}</h1>
                <p className="mt-2 text-[14px] text-white/85 leading-relaxed max-w-xl">{blurb}</p>
            </div>
            {children}
        </div>
    );
}

function EmptyBudget() {
    return (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="mq-empty-budget">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
                <div className="text-sm font-medium text-amber-900">Connect Xero to unlock the real answer</div>
                <p className="mt-1 text-[13px] text-amber-800 leading-relaxed">
                    We use your live income and expenses to base the number on reality — not a guess. Open the home screen and tap &quot;Connect Xero&quot; to fill this in.
                </p>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ shared */
function AnswerCard({ label, value, tone = "emerald", subtext, testId }) {
    return (
        <div className={`rounded-2xl border border-${tone}-200 bg-${tone}-50 p-5`} data-testid={testId}>
            <div className={`co-label text-${tone}-900`}>{label}</div>
            <div className={`mt-1 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-${tone}-950 tabular-nums`}>
                {value}
            </div>
            {subtext && <p className={`mt-2 text-[13px] text-${tone}-800 leading-relaxed`}>{subtext}</p>}
        </div>
    );
}

/* ============================================================
 * 1) HOW MUCH CAN I PAY MYSELF?
 * Sustainable owner draw = monthly_revenue - monthly_expenses - tax_reserve - safety_buffer
 * Defaults: 28% tax reserve (mid-bracket NZ), 15% cash safety buffer.
 * =========================================================== */
export function PortalPaySelf() {
    const { snap, loading } = useSnapshot();
    const [taxPct, setTaxPct] = useState(28);
    const [bufferPct, setBufferPct] = useState(15);

    const { monthly_revenue: revenue, monthly_expenses: expenses } = readBasics(snap);
    const preTax = Math.max(0, revenue - expenses);
    const taxReserve = preTax * (taxPct / 100);
    const afterTax = preTax - taxReserve;
    const buffer = afterTax * (bufferPct / 100);
    const safeDraw = Math.max(0, afterTax - buffer);
    const hasBudget = revenue > 0 || expenses > 0;

    return (
        <PageShell
            title="How much can I pay myself?"
            blurb="A sustainable monthly owner draw — after covering your business expenses, tax reserve and a healthy cash buffer."
            icon={Wallet}
            gradient="from-amber-500 to-orange-700"
            testId="mq-pay-self"
        >
            {loading ? (
                <div className="flex items-center gap-2 text-sm text-[#5E636E]"><Loader2 className="h-4 w-4 animate-spin" /> Loading your numbers…</div>
            ) : !hasBudget ? (
                <EmptyBudget />
            ) : (
                <>
                    <AnswerCard
                        label="Suggested monthly draw"
                        value={NZD.format(safeDraw)}
                        subtext={`Based on ${NZD.format(revenue)} revenue and ${NZD.format(expenses)} expenses per month, after ${taxPct}% tax reserve and ${bufferPct}% cash buffer.`}
                        testId="mq-pay-self-answer"
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <label className="flex flex-col text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                            Tax reserve
                            <input type="range" min="10" max="39" value={taxPct} onChange={(e) => setTaxPct(Number(e.target.value))} data-testid="mq-pay-self-tax" />
                            <span className="mt-0.5 text-sm text-[#0F1115] normal-case tracking-normal">{taxPct}%</span>
                        </label>
                        <label className="flex flex-col text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                            Cash buffer
                            <input type="range" min="0" max="40" value={bufferPct} onChange={(e) => setBufferPct(Number(e.target.value))} data-testid="mq-pay-self-buffer" />
                            <span className="mt-0.5 text-sm text-[#0F1115] normal-case tracking-normal">{bufferPct}%</span>
                        </label>
                    </div>
                    <div className="rounded-xl bg-white border border-[#E4E4E7] p-4 text-[13px] text-[#0F1115]">
                        <div className="co-label text-[#5E636E] mb-2">How we got there</div>
                        <ul className="space-y-1 tabular-nums">
                            <li>Monthly revenue: <span className="font-medium">{NZD.format(revenue)}</span></li>
                            <li>− Monthly expenses: <span className="font-medium">{NZD.format(expenses)}</span></li>
                            <li>= Profit before tax: <span className="font-medium">{NZD.format(preTax)}</span></li>
                            <li>− Tax reserve ({taxPct}%): <span className="font-medium">{NZD.format(taxReserve)}</span></li>
                            <li>− Cash buffer ({bufferPct}%): <span className="font-medium">{NZD.format(buffer)}</span></li>
                            <li className="pt-1 border-t border-[#E4E4E7]">Your safe draw: <span className="font-semibold">{NZD.format(safeDraw)}</span> / month</li>
                        </ul>
                    </div>
                </>
            )}
        </PageShell>
    );
}

/* ============================================================
 * 2) CAN I AFFORD TO HIRE?
 * Take proposed annual salary → monthly cost (incl. 12% oncosts default)
 * Compare against current monthly profit → verdict + months of runway.
 * =========================================================== */
export function PortalHire() {
    const { snap, loading } = useSnapshot();
    const [salary, setSalary] = useState(65000);
    const [oncostPct, setOncostPct] = useState(12);

    const { monthly_revenue: revenue, monthly_expenses: expenses } = readBasics(snap);
    const profit = Math.max(0, revenue - expenses);
    const monthlyCost = (salary * (1 + oncostPct / 100)) / 12;
    const remaining = profit - monthlyCost;
    const canAfford = remaining >= 0;
    const hasBudget = revenue > 0 || expenses > 0;

    return (
        <PageShell
            title="Can I afford to hire?"
            blurb="Model a new staff salary against your current monthly profit. We include ACC, KiwiSaver and holiday pay as on-costs so the answer's honest."
            icon={UserPlus}
            gradient="from-teal-500 to-emerald-800"
            testId="mq-hire"
        >
            {loading ? (
                <div className="flex items-center gap-2 text-sm text-[#5E636E]"><Loader2 className="h-4 w-4 animate-spin" /> Loading your numbers…</div>
            ) : !hasBudget ? (
                <EmptyBudget />
            ) : (
                <>
                    <div className="grid gap-3">
                        <label className="flex flex-col text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                            Proposed annual salary
                            <input
                                type="number"
                                inputMode="numeric"
                                value={salary}
                                min={0}
                                onChange={(e) => setSalary(Number(e.target.value) || 0)}
                                className="mt-1 rounded-md border border-[#E4E4E7] bg-white px-3 py-2 text-sm text-[#0F1115]"
                                data-testid="mq-hire-salary"
                            />
                        </label>
                        <label className="flex flex-col text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                            On-costs (ACC, KiwiSaver, holiday pay)
                            <input type="range" min="0" max="25" value={oncostPct} onChange={(e) => setOncostPct(Number(e.target.value))} data-testid="mq-hire-oncost" />
                            <span className="mt-0.5 text-sm text-[#0F1115] normal-case tracking-normal">{oncostPct}%</span>
                        </label>
                    </div>
                    <AnswerCard
                        label={canAfford ? "Yes — you can hire" : "Tight — you'd be running at a loss"}
                        value={NZD.format(monthlyCost)}
                        tone={canAfford ? "emerald" : "amber"}
                        subtext={canAfford
                            ? `That leaves ${NZD.format(remaining)} of monthly profit after their full cost. Comfortable head-room.`
                            : `That costs ${NZD.format(Math.abs(remaining))} more than your current monthly profit. Grow revenue by that much first, or start with a part-timer.`}
                        testId="mq-hire-answer"
                    />
                    <div className="rounded-xl bg-white border border-[#E4E4E7] p-4 text-[13px] text-[#0F1115]">
                        <div className="co-label text-[#5E636E] mb-2">How we got there</div>
                        <ul className="space-y-1 tabular-nums">
                            <li>Current monthly profit: <span className="font-medium">{NZD.format(profit)}</span></li>
                            <li>Annual salary + {oncostPct}% on-costs: <span className="font-medium">{NZD.format(salary * (1 + oncostPct / 100))} / yr</span></li>
                            <li>Monthly hire cost: <span className="font-medium">{NZD.format(monthlyCost)}</span></li>
                            <li className="pt-1 border-t border-[#E4E4E7]">Profit after hire: <span className={`font-semibold ${canAfford ? "text-emerald-800" : "text-amber-900"}`}>{NZD.format(remaining)}</span> / month</li>
                        </ul>
                    </div>
                </>
            )}
        </PageShell>
    );
}

/* ============================================================
 * 3) WHAT IF I RAISED MY PRICES?
 * Simple linear model: revenue × (1 + pct) with a churn assumption.
 * =========================================================== */
export function PortalPriceChange() {
    const { snap, loading } = useSnapshot();
    const [risePct, setRisePct] = useState(10);
    const [churnPct, setChurnPct] = useState(5);

    const { monthly_revenue: revenue, monthly_expenses: expenses } = readBasics(snap);
    const remainingShare = 1 - churnPct / 100;
    const newRevenue = revenue * (1 + risePct / 100) * remainingShare;
    const uplift = newRevenue - revenue;
    const newProfit = newRevenue - expenses;
    const oldProfit = revenue - expenses;
    const profitLift = newProfit - oldProfit;
    const hasBudget = revenue > 0;

    return (
        <PageShell
            title="What if I raised my prices?"
            blurb="See the revenue and profit lift from a price rise — including a realistic churn assumption for the customers you might lose."
            icon={TrendingUp}
            gradient="from-fuchsia-500 to-pink-700"
            testId="mq-price-change"
        >
            {loading ? (
                <div className="flex items-center gap-2 text-sm text-[#5E636E]"><Loader2 className="h-4 w-4 animate-spin" /> Loading your numbers…</div>
            ) : !hasBudget ? (
                <EmptyBudget />
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="flex flex-col text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                            Price rise
                            <input type="range" min="0" max="30" value={risePct} onChange={(e) => setRisePct(Number(e.target.value))} data-testid="mq-price-rise" />
                            <span className="mt-0.5 text-sm text-[#0F1115] normal-case tracking-normal">+{risePct}%</span>
                        </label>
                        <label className="flex flex-col text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                            Customer churn
                            <input type="range" min="0" max="30" value={churnPct} onChange={(e) => setChurnPct(Number(e.target.value))} data-testid="mq-price-churn" />
                            <span className="mt-0.5 text-sm text-[#0F1115] normal-case tracking-normal">−{churnPct}%</span>
                        </label>
                    </div>
                    <AnswerCard
                        label="Extra profit per month"
                        value={`${profitLift >= 0 ? "+" : "−"}${NZD.format(Math.abs(profitLift))}`}
                        tone={profitLift >= 0 ? "emerald" : "rose"}
                        subtext={profitLift >= 0
                            ? `New monthly revenue rises to ${NZD.format(newRevenue)} even after losing ${PCT.format(churnPct / 100)} of customers.`
                            : `Churn eats the price rise — you'd be worse off. Try a smaller rise or a churn scenario under ${churnPct}%.`}
                        testId="mq-price-answer"
                    />
                    <div className="rounded-xl bg-white border border-[#E4E4E7] p-4 text-[13px] text-[#0F1115]">
                        <div className="co-label text-[#5E636E] mb-2">How we got there</div>
                        <ul className="space-y-1 tabular-nums">
                            <li>Current monthly revenue: <span className="font-medium">{NZD.format(revenue)}</span></li>
                            <li>After +{risePct}% and −{churnPct}% churn: <span className="font-medium">{NZD.format(newRevenue)}</span></li>
                            <li>Revenue uplift: <span className="font-medium">{NZD.format(uplift)}</span></li>
                            <li className="pt-1 border-t border-[#E4E4E7]">Profit lift: <span className="font-semibold">{NZD.format(profitLift)}</span> / month</li>
                        </ul>
                    </div>
                </>
            )}
        </PageShell>
    );
}

/* ============================================================
 * 4) GROWTH PLAN
 * Set a target next-year revenue → monthly milestones + monthly delta.
 * =========================================================== */
export function PortalGrowthPlan() {
    const { snap, loading } = useSnapshot();
    const [growthPct, setGrowthPct] = useState(20);

    const { monthly_revenue, annual_revenue } = readBasics(snap);
    const currentAnnual = annual_revenue || (monthly_revenue * 12);
    const targetAnnual = currentAnnual * (1 + growthPct / 100);
    const targetMonthly = targetAnnual / 12;
    const monthlyDelta = targetMonthly - (currentAnnual / 12);
    const milestones = useMemo(() => {
        // Ramp linearly: each month adds an equal share of delta.
        return Array.from({ length: 12 }, (_, i) => ({
            month: i + 1,
            target: (currentAnnual / 12) + monthlyDelta * ((i + 1) / 12),
        }));
    }, [currentAnnual, monthlyDelta]);
    const hasBudget = currentAnnual > 0;

    return (
        <PageShell
            title="Growth plan"
            blurb="Set next year's revenue target. We'll break it into monthly milestones so you always know if you're ahead or behind."
            icon={Rocket}
            gradient="from-indigo-500 to-blue-800"
            testId="mq-growth-plan"
        >
            {loading ? (
                <div className="flex items-center gap-2 text-sm text-[#5E636E]"><Loader2 className="h-4 w-4 animate-spin" /> Loading your numbers…</div>
            ) : !hasBudget ? (
                <EmptyBudget />
            ) : (
                <>
                    <label className="flex flex-col text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                        Growth target
                        <input type="range" min="0" max="100" value={growthPct} onChange={(e) => setGrowthPct(Number(e.target.value))} data-testid="mq-growth-target" />
                        <span className="mt-0.5 text-sm text-[#0F1115] normal-case tracking-normal">+{growthPct}% next year</span>
                    </label>
                    <AnswerCard
                        label="Target annual revenue"
                        value={NZD.format(targetAnnual)}
                        tone="indigo"
                        subtext={`That's ${NZD.format(monthlyDelta)} more per month, on average, than you make today.`}
                        testId="mq-growth-answer"
                    />
                    <div className="rounded-xl bg-white border border-[#E4E4E7] p-4 text-[13px] text-[#0F1115]">
                        <div className="co-label text-[#5E636E] mb-2">Monthly milestones</div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 tabular-nums">
                            {milestones.map((m) => (
                                <div key={m.month} className="rounded-md bg-[#F5F4EF] px-3 py-2">
                                    <div className="text-[11px] uppercase tracking-[0.08em] text-[#5E636E]">Month {m.month}</div>
                                    <div className="text-[13px] font-medium text-[#0F1115]">{NZD.format(m.target)}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </PageShell>
    );
}
